import React from "react";
import {
  MigrationStatistics,
  calculateFilesStatistics,
} from "../../models/Statistics/MigrationStatistics";
import StatisticsCard from "../common/StatisticsCard";
import { CHART_COLORS } from "../../constants/ChartColors";

interface OverallStatisticsCardProps {
  statistics: MigrationStatistics;
  displayOpenCaseAndIntakeStatistics: boolean;
  displayClosedCaseAndIntakeStatistics: boolean;
}

const OverallStatisticsCard: React.FC<OverallStatisticsCardProps> = ({
  statistics,
  displayOpenCaseAndIntakeStatistics,
  displayClosedCaseAndIntakeStatistics
}) => {
  const {
    casesStatistics,
    intakesStatistics,
    providersStatistics,
    firmDocumentFoldersStatistics,
    templateFilesStatistics,
  } = statistics;
  const filesStatistics = calculateFilesStatistics(statistics, displayOpenCaseAndIntakeStatistics, displayClosedCaseAndIntakeStatistics);

  let totalFinishedMigrations =
    casesStatistics.caseFoldersMigrated +
    providersStatistics.providersMigrated +
    firmDocumentFoldersStatistics.foldersMigrated +
    templateFilesStatistics.templateLibrariesMigrated;
  if (displayOpenCaseAndIntakeStatistics) totalFinishedMigrations += casesStatistics.openCasesMigrated + intakesStatistics.openIntakesMigrated;
  if (displayClosedCaseAndIntakeStatistics) totalFinishedMigrations += casesStatistics.closedCasesMigrated + intakesStatistics.closedIntakesMigrated;

  let totalMigrationsInProgress =
    casesStatistics.caseFoldersInProgress +
    providersStatistics.providersInProgress +
    firmDocumentFoldersStatistics.foldersInProgress +
    templateFilesStatistics.templateLibrariesInProgress;
  if (displayOpenCaseAndIntakeStatistics) totalMigrationsInProgress += casesStatistics.openCasesInProgress + intakesStatistics.openIntakesInProgress;
  if (displayClosedCaseAndIntakeStatistics) totalMigrationsInProgress += casesStatistics.closedCasesInProgress + intakesStatistics.closedIntakesInProgress;

  let totalFailedMigrations =
    casesStatistics.caseFoldersInterrupted +
    providersStatistics.providersInterrupted +
    firmDocumentFoldersStatistics.foldersInterrupted +
    templateFilesStatistics.templateLibrariesInterrupted;
  if (displayOpenCaseAndIntakeStatistics) totalFailedMigrations += casesStatistics.openCasesInterrupted + intakesStatistics.openIntakesInterrupted;
  if (displayClosedCaseAndIntakeStatistics) totalFailedMigrations += casesStatistics.closedCasesInterrupted + intakesStatistics.closedIntakesInterrupted;

  let totalNumberOfMigrations =
    casesStatistics.numberOfCaseFolders +
    providersStatistics.numberOfProviders +
    firmDocumentFoldersStatistics.numberOfFolders +
    templateFilesStatistics.numberOfTemplateLibraries;
  if (displayOpenCaseAndIntakeStatistics) totalNumberOfMigrations += casesStatistics.numberOfOpenCases + intakesStatistics.numberOfOpenIntakes;
  if (displayClosedCaseAndIntakeStatistics) totalNumberOfMigrations += casesStatistics.numberOfClosedCases + intakesStatistics.numberOfClosedIntakes;

  let totalResolvedMigrations = casesStatistics.caseFoldersResolved;
  if (displayOpenCaseAndIntakeStatistics) totalResolvedMigrations += casesStatistics.openCasesResolved + intakesStatistics.openIntakesResolved;
  if (displayClosedCaseAndIntakeStatistics) totalResolvedMigrations += casesStatistics.closedCasesResolved + intakesStatistics.closedIntakesResolved;

  let totalAcknowledgedMigrations = casesStatistics.caseFoldersAcknowledged;
  if (displayOpenCaseAndIntakeStatistics) totalAcknowledgedMigrations += casesStatistics.openCasesAcknowledged + intakesStatistics.openIntakesAcknowledged;
  if (displayClosedCaseAndIntakeStatistics) totalAcknowledgedMigrations += casesStatistics.closedCasesAcknowledged + intakesStatistics.closedIntakesAcknowledged;

  let filesWithFlaggedStatus = casesStatistics.caseFoldersFlagged;
  if (displayOpenCaseAndIntakeStatistics) filesWithFlaggedStatus += casesStatistics.openCasesFlagged + intakesStatistics.openIntakesFlagged;
  if (displayClosedCaseAndIntakeStatistics) filesWithFlaggedStatus += casesStatistics.closedCasesFlagged + intakesStatistics.closedIntakesFlagged;

  const totalFlaggedMigrations = filesWithFlaggedStatus - (totalResolvedMigrations + totalAcknowledgedMigrations);

  const totalNotStartedMigrations =
    totalNumberOfMigrations -
    totalFinishedMigrations -
    totalFailedMigrations -
    totalMigrationsInProgress -
    totalFlaggedMigrations -
    totalResolvedMigrations -
    totalAcknowledgedMigrations;

  const caseChartData = [
    {
      name: "Migrated",
      value: totalFinishedMigrations,
      color: CHART_COLORS.migrated,
    },
    {
      name: "In Progress",
      value: totalMigrationsInProgress,
      color: CHART_COLORS.inProgress,
    },
    {
      name: "Interrupted",
      value: totalFailedMigrations,
      color: CHART_COLORS.interrupted,
    },
    {
      name: "Not Started",
      value: totalNotStartedMigrations,
      color: CHART_COLORS.notStarted,
    },
    {
      name: "Flagged",
      value: totalFlaggedMigrations,
      color: CHART_COLORS.flagged,
    },
    {
      name: "Allowed",
      value: totalResolvedMigrations,
      color: CHART_COLORS.allowed,
    },
    {
      name: "Acknowledged",
      value: totalAcknowledgedMigrations,
      color: CHART_COLORS.acknowledged,
    },
  ];
  const filesChartData = [
    {
      name: "Migrated",
      value: filesStatistics.TotalFinishedFiles,
      color: CHART_COLORS.migrated,
    },
    {
      name: "Not Found",
      value: filesStatistics.TotalNotFoundFiles,
      color: CHART_COLORS.inProgress,
    },
    {
      name: "Not Found (Marked For Retry)",
      value: filesStatistics.TotalNotFoundMarkedForRetryFiles,
      color: CHART_COLORS.acknowledged,
    },
    {
      name: "Interrupted Initial",
      value: filesStatistics.TotalFailedInitialFiles,
      color: CHART_COLORS.interrupted,
    },
    {
      name: "Interrupted Delta",
      value: filesStatistics.TotalFailedDeltaFiles,
      color: CHART_COLORS.interruptedDelta,
    },
    {
      name: "Modified",
      value: filesStatistics.TotalModifiedFiles,
      color: CHART_COLORS.modified,
    },
    {
      name: "Not Started",
      value: filesStatistics.TotalNotStartedFiles,
      color: CHART_COLORS.notStarted,
    },
    {
      name: "Blocked by conflict",
      value: filesStatistics.TotalBlockedByConflictFiles,
      color: CHART_COLORS.blockedByConflict,
    },
    {
      name: "Acknowledged conflict",
      value: filesStatistics.TotalAcknowledgedConflictFiles,
      color: CHART_COLORS.acknowledgedConflict,
    },
    {
      name: "Flagged",
      value: filesStatistics.TotalFlaggedFiles,
      color: CHART_COLORS.flagged,
    },
    {
      name: "Skipped Duplicate",
      value: filesStatistics.TotalSkippedDuplicateFiles,
      color: CHART_COLORS.skippedDuplicate,
    },
  ];

  return (
    <StatisticsCard
      migrationChartData={{ data: caseChartData, label: "Migrations" }}
      filesChartData={{ data: filesChartData, label: "Files" }}
      title={"Migration Statistics"}
      totalFilesSizeInGb={filesStatistics.TotalFilesSizeInGb}
      totalMigratedFilesSizeInGb={filesStatistics.MigratedFilesSizeInGb}
      totalFilesSizeInMb={filesStatistics.TotalFilesSizeInMb}
      totalMigratedFilesSizeInMb={filesStatistics.MigratedFilesSizeInMb}
      notFoundFilesSizeInGb={filesStatistics.NotFoundFilesSizeInGb}
      notFoundFilesSizeInMb={filesStatistics.NotFoundFilesSizeInMb}
      interruptedFilesSizeInGb={filesStatistics.InterruptedFilesSizeInGb}
      interruptedFilesSizeInMb={filesStatistics.InterruptedFilesSizeInMb}
      skippedDuplicateFilesSizeInGb={filesStatistics.SkippedDuplicateFilesSizeInGb}
      skippedDuplicateFilesSizeInMb={filesStatistics.SkippedDuplicateFilesSizeInMb}
      showFlaggedSizeInfo={false}
    />
  );
};

export default OverallStatisticsCard;
